// Admin Order Management Types
export interface AdminOrderList {
  id: string;
  orderNumber: string;
  fullName: string;
  email: string;
  status: string;
  paymentStatus: string;
  paymentMethodType?: string; // "Stripe", "Alif", "DC Card", "PayPal", "Bank Transfer"
  totalPrice: number;
  totalAmount?: number; // Normalized alias used by shared order-summary views
  shippingCost: number;
  tax: number;
  fees: number;
  finalTotal: number;
  totalCarrierCargoFee?: number | null;
  pickupLocation?: string;
  itemCount: number;
  orderedDate: string;
  orderedDateTime: string;
  cargoCompanyId?: string;
  cargoCompanyName?: string;
  promotionCode?: string;
  promotionDiscountAmount?: number;
  promotionCodeId?: string;
  // TJS (Somoni) conversion
  exchangeRateAtOrder?: number | null;
  totalPriceTjs?: number | null;
  totalAmountTjs?: number | null; // Normalized alias used by shared order-summary views
  shippingCostTjs?: number | null;
  taxTjs?: number | null;
  feesTjs?: number | null;
  discountAmount?: number | null;
  discountAmountTjs?: number | null;
  finalTotalTjs?: number | null;

  // Referral info
  isReferredUser?: boolean;
  referredByUserName?: string;
  referralCouponUsed?: string;

  // Soft delete / Archive
  isDeleted?: boolean;
  isArchived?: boolean;
  archivedAt?: string;
}

export interface AdminOrderItem {
  id: string;
  productId: string;
  title: string;
  price: number;
  quantity: number;
  image: string;
  sku: string;
  subtotal: number;
  retailer?: string;
  itemStatusId?: number;
  itemStatusName?: string;
  itemStatusDisplayName?: string;
  itemStatusColor?: string;
  notes?: string;
  trackingNumber?: string;
  estimatedDelivery?: string;
  orderedAt?: string;
  shippedAt?: string;
  hasTrackingNumber?: boolean;
  hasNotes?: boolean;
  isShipped?: boolean;
  isOrdered?: boolean;
  estimatedWeight?: number; // Estimated weight in kg before sending to cargo company
  cargoWeight?: number; // Actual cargo company weight (set by backend after weighing)
  // TJS (Somoni) conversion
  priceTjs?: number | null;
  subtotalTjs?: number | null;
}

export interface AdminDeliveryInfo {
  fullName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  email: string;
  company?: string;
  address2?: string;
}

export interface AdminPaymentInfo {
  paymentMethod: string; // Display name: "Credit Card", "Alif", "DC Card", etc.
  paymentMethodType?: number; // 1=Stripe, 2=Alif, 3=DcCard, 4=PayPal, 5=BankTransfer
  transactionId: string; // PaymentIntentId for Stripe, or manual transaction ID
  paymentDate?: string;
  cardLast4: string;
  cardBrand: string;
  paymentStatus: string;
  amountPaid: number;
  amountPaidTjs?: number | null;
  refundAmount: number;
  // Manual payment fields (for Alif/DC Card)
  manualPaymentTransactionId?: string; // Customer's transaction ID
  manualPaymentCustomerPhone?: string; // Customer's phone number
  manualPaymentScreenshotUrl?: string; // Screenshot URL
  manualPaymentVerifiedAt?: string;
  manualPaymentVerifiedBy?: string; // Admin user ID who verified
}

export interface AdminOrderHistory {
  action: string;
  description: string;
  performedBy: string;
  timestamp: string;
  oldValue: string;
  newValue: string;
}

export interface AdminOrderDetail extends AdminOrderList {
  items: AdminOrderItem[];
  deliveryInfo: AdminDeliveryInfo;
  paymentInfo: AdminPaymentInfo;
  notes: string;
  assignedTo: string;
  assignedAt?: string;
  orderHistory: AdminOrderHistory[];
  cargoCompanyId?: string;
  cargoCompanyName?: string;
  promotionCode?: string;
  promotionDiscountAmount?: number;
  promotionCodeId?: string;
}

export interface AdminOrderFilters {
  search?: string;
  status?: string;
  paymentStatus?: string;
  startDate?: string;
  endDate?: string;
  customerId?: string;
  assignedTo?: string;
  minAmount?: number;
  maxAmount?: number;
  pickUpLocation?: string;
  includeArchived?: boolean;
  archivedOnly?: boolean;
  includeDeleted?: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface AdminOrderStatistics {
  totalOrders: number;
  pendingOrders: number;
  processingOrders: number;
  shippedOrders: number;
  deliveredOrders: number;
  cancelledOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  ordersToday: number;
  ordersThisWeek: number;
  ordersThisMonth: number;
  revenueToday: number;
  revenueThisWeek: number;
  revenueThisMonth: number;
}

export interface PaginatedAdminOrdersResponse {
  items: AdminOrderList[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Request DTOs
export interface UpdateOrderStatusRequest {
  newStatusId: number;
  statusChangeNotes?: string; // Maps to backend StatusChangeNotes
  trackingNumber?: string;
  estimatedDelivery?: string;
  performedBy?: string;
  sendNotification?: boolean;
  orderItemIds?: string[]; // IDs of selected order items to include in email template (maps to backend OrderItemIds)
  sortOrder?: number; // Optional sort order for template selection
  itemStatusId?: number; // Optional item status ID to cascade to all order items
}

export interface UpdateOrderPaymentStatusRequest {
  paymentStatus: string;
  transactionId?: string;
  paymentDate?: string;
  notes?: string;
}

export interface AssignOrderRequest {
  assignedTo: string;
  notes?: string;
}

export interface AddOrderNoteRequest {
  note: string;
  isInternal?: boolean;
}

export interface BulkUpdateOrderStatusRequest {
  orderNumbers: string[];
  status: string;
  notes?: string;
  itemStatusId?: number;
  sendNotification?: boolean;
}

// Comprehensive Order Update Request
export interface UpdateOrderComprehensiveRequest {
  totalPrice?: number;
  shippingCost?: number;
  tax?: number;
  finalTotal?: number;
  totalCarrierCargoFee?: number;
  pickupLocation?: string;
  deliveryInstructions?: string;
  paymentStatus?: string;
  cargoCompanyId?: string | null;
  amount?: number;
  currency?: string;
  notes?: string;
  sendNotification?: boolean;
  performedBy?: string;
}

// Order Status from Server
export interface OrderStatusDto {
  id: number;
  name: string;
  displayName: string;
  description: string;
  color: string;
  icon: string;
  sortOrder: number;
  isActive: boolean;
  canTransitionTo: number[];
  requiresTrackingNumber: boolean;
  requiresNote: boolean;
  createdAt: string;
  updatedAt: string;
  orderCount: number;
}

// Order Status Options
export const ORDER_STATUSES = [
  "Pending",
  "Processing",
  "Shipped",
  "Delivered",
  "Cancelled",
  "Refunded",
] as const;

export const PAYMENT_STATUSES = [
  "pending_verification",
  "processing",
  "succeeded",
  "failed",
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

// Order Details and History Types (using backend DTOs)
export interface OrderDetails {
  id: string;
  orderNumber: string;
  fullName: string;
  email: string;
  status: string;
  paymentStatus: string | null;
  totalPrice: number;
  shippingCost: number;
  tax: number;
  fees: number | null;
  finalTotal: number;
  pickupLocation: string;
  itemCount: number;
  orderedDate: string;
  orderedDateTime: string | null;
  totalCarrierCargoFee: number | null;
  discountAmount: number | null;
  promotionCode?: string;
  promotionDiscountAmount?: number;
  promotionCodeId?: string;
  // TJS (Somoni) conversion
  exchangeRateAtOrder?: number | null;
  totalPriceTjs?: number | null;
  shippingCostTjs?: number | null;
  finalTotalTjs?: number | null;
  taxTjs?: number | null;
  feesTjs?: number | null;
  discountAmountTjs?: number | null;
  updatedAt: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  phoneNumber: string;
  deliveryInstructions: string;
  deliveryInfo: any | null;
  paymentInfo: AdminPaymentInfo;
  // Referral info
  isReferredUser?: boolean;
  referredByUserName?: string;
  referralCouponUsed?: string;
}

export interface OrderItemDetails {
  id: string;
  productId: string;
  productName: string;
  productSku: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  status: string;
  trackingNumber?: string;
  notes?: string;
}

export interface DeliveryInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface PaymentInfo {
  id: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  transactionId?: string;
  status: string;
  createdAt: string;
}

export interface OrderHistoryEntry {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  description: string;
  performedBy?: string;
  createdAt: string;
  note?: string;
  oldValuesJson?: string;
  newValuesJson?: string;
}

// Paginated History Response
export interface PaginatedHistoryResponse {
  items: OrderHistoryEntry[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}
